import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { getStats, trackCalculation, trackVisit } from "../firebase/functions";

const Context = createContext(null);

const LS_KEYS = {
  theme: "ugc_theme",
  selection: "ugc_selection",
  freeCalcCount: "ugc_free_calc_count",
};

const DEFAULT_SELECTION = {
  step: "select",
  university: null,
  degreeLevel: null,
};

function safeParseSelection(raw) {
  if (!raw) return DEFAULT_SELECTION;

  try {
    const parsed = JSON.parse(raw);

    return {
      step: parsed?.step === "calc" ? "calc" : "select",
      university: parsed?.university ?? null,
      degreeLevel: parsed?.degreeLevel ?? null,
    };
  } catch {
    return DEFAULT_SELECTION;
  }
}

export function AppProvider({ children }) {
  const [theme, setTheme] = useState(() => localStorage.getItem(LS_KEYS.theme) || "dark");

  const [selection, setSelection] = useState(() => {
    const raw = localStorage.getItem(LS_KEYS.selection);
    const sel = safeParseSelection(raw);

    if (raw && sel === DEFAULT_SELECTION) {
      try {
        JSON.parse(raw);
      } catch {
        localStorage.removeItem(LS_KEYS.selection);
      }
    }

    return sel;
  });

  // DB-backed stats (from Firestore via Functions)
  const [stats, setStats] = useState({ visits: 0, calculations: 0 });
  const [statsLoading, setStatsLoading] = useState(true);
  
  const [freeCalcCount, setFreeCalcCount] = useState(() => {
    const n = Number(localStorage.getItem(LS_KEYS.freeCalcCount) || "0");
    return Number.isFinite(n) ? n : 0;
  });

  // Persist + apply theme
  useEffect(() => {
    localStorage.setItem(LS_KEYS.theme, theme);
    document.documentElement.classList.toggle("dark", theme === "dark");
  }, [theme]);

  useEffect(() => {
    localStorage.setItem(LS_KEYS.freeCalcCount, String(freeCalcCount));
  }, [freeCalcCount]);

  // Persist selection
  useEffect(() => {
    localStorage.setItem(LS_KEYS.selection, JSON.stringify(selection));
  }, [selection]);

  const refreshStats = useCallback(async () => {
    try {
      const data = await getStats();
      setStats({
        visits: Number(data?.visits || 0),
        calculations: Number(data?.calculations || 0),
      });
    } catch {
      // never break UI
    } finally {
      setStatsLoading(false);
    }
  }, []);

  // Track visit + load stats once per app load
  useEffect(() => {
    trackVisit();
    refreshStats();
  }, [refreshStats]);

  const incrementCalculations = useCallback(async (opts = {}) => {
    const { countFree = false } = opts;

    if (countFree) setFreeCalcCount((c) => c + 1);

    trackCalculation();
    await refreshStats();
  }, [refreshStats]);

  const value = useMemo(
    () => ({
      theme,
      setTheme,
      selection,
      setSelection,
      stats,
      statsLoading,
      refreshStats,
      incrementCalculations,
      freeCalcCount,
      setFreeCalcCount,
    }),
    [theme, selection, stats, statsLoading, refreshStats, incrementCalculations, freeCalcCount]
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useApp() {
  const ctx = useContext(Context);
  if (!ctx) throw new Error("useApp must be used within AppProvider");
  return ctx;
}
